import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useCreateCryptoHolding, useUpdateCryptoHolding } from '@cryptoHooks';
import { CryptoSearchDropdown } from '@/components/crypto-search-dropdown/CryptoSearchDropdown';
import {CryptoHolding, CryptoWallet} from "@/integrations/supabase/crypto.models.ts";

export const holdingSchema = z.object({
  wallet_id: z.string().uuid('Select a wallet'),
  symbol: z.string().min(1, 'Symbol is required'),
  name: z.string().min(1, 'Name is required'),
  amount: z.number().positive('Amount must be greater than 0'),
  cost_basis: z.number().min(0, 'Cost basis must be 0 or greater'),
  currency: z.string().min(1),
});

type HoldingFormValues = z.infer<typeof holdingSchema>;

interface HoldingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wallets: CryptoWallet[];
  onSuccess: () => void;
  holding?: CryptoHolding;
}

export function HoldingDialog({
  open,
  onOpenChange,
  wallets,
  onSuccess,
  holding,
}: HoldingDialogProps) {
  const isEditMode = !!holding;
  const createHolding = useCreateCryptoHolding();
  const updateHolding = useUpdateCryptoHolding();

  const form = useForm<HoldingFormValues>({
    resolver: zodResolver(holdingSchema),
    defaultValues: {
      wallet_id: '',
      symbol: '',
      name: '',
      amount: undefined,
      cost_basis: undefined,
      currency: 'USD',
    },
  });

  // Pre-populate form when in edit mode
  useEffect(() => {
    if (holding && open) {
      form.reset({
        wallet_id: holding.wallet_id,
        symbol: holding.symbol,
        name: holding.name,
        amount: Number(holding.amount),
        cost_basis: Number(holding.cost_basis),
        currency: holding.currency,
      });
    } else if (!holding && open) {
      form.reset({
        wallet_id: '',
        symbol: '',
        name: '',
        amount: undefined,
        cost_basis: undefined,
        currency: 'USD',
      });
    }
  }, [holding, open, form]);

  const { isSubmitting } = form.formState;

  const onSubmit = async (values: HoldingFormValues) => {
    try {
      if (isEditMode) {
        await updateHolding.mutateAsync({
          id: holding.id,
          wallet_id: values.wallet_id,
          symbol: values.symbol,
          name: values.name,
          amount: values.amount,
          cost_basis: values.cost_basis,
          currency: values.currency,
        });
      } else {
        await createHolding.mutateAsync({
          wallet_id: values.wallet_id,
          symbol: values.symbol,
          name: values.name,
          amount: values.amount,
          cost_basis: values.cost_basis,
          currency: values.currency,
        });
      }
      form.reset();
      onSuccess();
    } catch {
      toast.error(
        isEditMode
          ? 'Failed to update holding. Please try again.'
          : 'Failed to create holding. Please try again.'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Holding' : 'Add Holding'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="wallet_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Wallet *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a wallet" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {wallets.map((wallet) => (
                        <SelectItem key={wallet.id} value={wallet.id}>
                          {wallet.provider_name} — {wallet.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Crypto search dropdown — auto-populates symbol and name */}
            <FormItem>
              <FormLabel>Cryptocurrency *</FormLabel>
              <CryptoSearchDropdown
                onSelect={({ symbol, name }) => {
                  form.setValue('symbol', symbol, { shouldValidate: true });
                  form.setValue('name', name, { shouldValidate: true });
                }}
                disabled={isSubmitting}
              />
            </FormItem>
            <FormField
              control={form.control}
              name="symbol"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Symbol *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. BTC, ETH" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Bitcoin, Ethereum" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      placeholder="0.00"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                      }
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cost_basis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cost Basis *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      placeholder="0.00"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                      }
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <FormControl>
                    <Input placeholder="USD" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? isEditMode
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditMode
                  ? 'Save Changes'
                  : 'Add Holding'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
